import * as React from "react";
import {useState, useEffect} from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {POST, GET} from "APIclient";
import properties from "constants/ApplicationConstants";

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes toastIn {
    from { opacity: 0; transform: translateX(40px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .aco-page * { box-sizing: border-box; }
  .aco-page { font-family: 'DM Sans', 'Segoe UI', sans-serif; }

  .aco-datepicker .MuiInputBase-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.88rem !important;
    border-radius: 10px !important;
    background: #f8fafc !important;
    border: 1.5px solid #e2e8f0 !important;
    padding: 2px 8px !important;
  }
  .aco-datepicker .MuiOutlinedInput-notchedOutline { border: none !important; }
  .aco-datepicker .MuiInputBase-root:focus-within {
    border-color: #3b55e6 !important;
    box-shadow: 0 0 0 3px rgba(59,85,230,0.10) !important;
    background: #fff !important;
  }
  .aco-datepicker .MuiInputLabel-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.85rem !important;
  }
  .aco-date-row { transition: background 0.15s; }
  .aco-date-row:hover { background: #f8faff !important; }

  .aco-btn-primary:not(:disabled):hover { transform: translateY(-1px); }
  .aco-btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }

  /* ── Responsive ── */
  .aco-page-wrap {
    padding: 10px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    min-height: 100%;
  }
  @media (max-width: 479px) {
    .aco-page-wrap { padding: 6px !important; }
  }

  .aco-card {
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(67,97,238,0.10), 0 4px 16px rgba(0,0,0,0.06);
    background: #fff;
    width: 100%;
    max-width: 560px;
    animation: slideUp 0.45s cubic-bezier(.22,1,.36,1) both;
  }
  @media (max-width: 479px) {
    .aco-card { border-radius: 14px; }
  }

  .aco-header-inner { padding: 14px 22px 12px; }
  @media (max-width: 479px) {
    .aco-header-inner { padding: 12px 16px 10px; }
  }

  .aco-body-inner { padding: 16px 22px 20px; }
  @media (max-width: 479px) {
    .aco-body-inner { padding: 12px 14px 16px; }
  }

  /* Employee + checkbox row */
  .aco-emp-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }
  .aco-emp-select-wrap { flex: 1; min-width: 200px; }
  @media (max-width: 479px) {
    .aco-emp-select-wrap { min-width: 100%; }
    .aco-checkbox-wrap { min-width: 100%; }
  }

  /* Dates table */
  @media (max-width: 479px) {
    .aco-dates-th { padding: 5px 8px !important; font-size: 0.63rem !important; }
    .aco-dates-td { padding: 6px 8px !important; }
  }

  /* Toast */
  .aco-toast {
    position: fixed;
    top: 24px;
    right: 24px;
    z-index: 9999;
    padding: 14px 20px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.87rem;
    font-weight: 600;
    font-family: inherit;
    box-shadow: 0 8px 32px rgba(0,0,0,0.14);
    animation: toastIn 0.35s cubic-bezier(.22,1,.36,1) both;
    max-width: 340px;
  }
  @media (max-width: 479px) {
    .aco-toast {
      top: auto !important;
      bottom: 16px;
      right: 12px;
      left: 12px;
      max-width: 100%;
      font-size: 0.82rem;
    }
  }
`;

const s = {
  page: {
    background: "linear-gradient(135deg, #f0f4ff 0%, #fafbff 60%, #eef2ff 100%)",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif"
  },
  header: {
    background: "linear-gradient(135deg, #3b55e6 0%, #5e75f5 100%)",
    position: "relative",
    overflow: "hidden"
  },
  hCircle1: {
    position: "absolute",
    width: 130,
    height: 130,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.08)",
    top: -40,
    right: -30,
    pointerEvents: "none"
  },
  hCircle2: {
    position: "absolute",
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
    bottom: -15,
    left: 15,
    pointerEvents: "none"
  },
  headerIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 7,
    backdropFilter: "blur(4px)"
  },
  headerTitle: {
    color: "#fff",
    fontSize: "1.1rem",
    fontWeight: 700,
    margin: 0,
    letterSpacing: "-0.3px",
    fontFamily: properties.fontFamily
  },
  headerSub: {
    color: "rgba(255,255,255,0.72)",
    fontFamily: properties.fontFamily,
    fontSize: "0.76rem",
    marginTop: 2,
    fontWeight: 400
  },

  fieldGroup: {marginBottom: 12},
  label: {
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#4a5568",
    marginBottom: 5,
    letterSpacing: "0.4px",
    textTransform: "uppercase"
  },
  required: {color: "#e53e3e", marginLeft: 2},

  selectWrap: {position: "relative"},
  select: {
    width: "100%",
    padding: "9px 36px 9px 12px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: "0.88rem",
    color: "#2d3748",
    outline: "none",
    fontFamily: "inherit",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    transition: "border-color 0.2s, box-shadow 0.2s"
  },
  selectError: {borderColor: "#fc8181", background: "#fff5f5"},
  selectFocus: {
    borderColor: "#3b55e6",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(59,85,230,0.10)"
  },
  selectArrow: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    color: "#a0aec0"
  },

  textarea: {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: "0.88rem",
    color: "#2d3748",
    outline: "none",
    fontFamily: "inherit",
    resize: "vertical",
    minHeight: 62,
    transition: "border-color 0.2s, box-shadow 0.2s"
  },
  textareaError: {borderColor: "#fc8181", background: "#fff5f5"},
  textareaFocus: {
    borderColor: "#3b55e6",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(59,85,230,0.10)"
  },

  errorMsg: {
    color: "#e53e3e",
    fontSize: "0.72rem",
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontWeight: 500
  },

  divider: {height: 1, background: "#edf2f7", margin: "12px 0"},

  sectionLabel: {
    fontSize: "0.70rem",
    fontWeight: 700,
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 8
  },

  datesTable: {width: "100%", borderCollapse: "collapse"},
  datesTh: {
    padding: "6px 12px",
    background: "#f8fafc",
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1.5px solid #e2e8f0",
    textAlign: "left"
  },
  datesTd: {padding: "8px 12px", borderBottom: "1px solid #f0f4f8"},
  dayBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "#ebf4ff",
    color: "#3b55e6",
    fontSize: "0.72rem",
    fontWeight: 700
  },

  autoApproveBadge: checked => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 12px",
    borderRadius: 20,
    fontSize: "0.78rem",
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
    transition: "all 0.2s",
    whiteSpace: "nowrap",
    background: checked ? "#ebf8ff" : "#f8fafc",
    color: checked ? "#2b6cb0" : "#718096",
    border: `1.5px solid ${checked ? "#bee3f8" : "#e2e8f0"}`
  }),

  btnPrimary: {
    width: "100%",
    padding: "10px 28px",
    borderRadius: 10,
    border: "none",
    background: "linear-gradient(135deg, #3b55e6 0%, #5e75f5 100%)",
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.88rem",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "opacity 0.15s, transform 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    boxShadow: "0 4px 16px rgba(59,85,230,0.28)",
    marginTop: 14
  },

  toastSuccess: {background: "#f0fff4", color: "#276749", border: "1.5px solid #9ae6b4"},
  toastError: {background: "#fff5f5", color: "#c53030", border: "1.5px solid #feb2b2"}
};

function FieldError({msg}) {
  if (!msg) return null;
  return (
    <div style={s.errorMsg}>
      <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      {msg}
    </div>
  );
}

export default function AssignCompensatoryOff() {
  const [empId, setEmpId] = useState(Number(window.sessionStorage.getItem("empId")));
  const [cOffLeaves, setCOffLeaves] = useState("");
  const [dates, setDates] = useState([dayjs()]);
  const [reason, setReason] = useState("");
  const [isAutoApproved, setIsAutoApproved] = useState(true);
  const [employeeList, setEmployeeList] = useState([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const [toast, setToast] = useState({open: false, color: "", message: ""});
  const [errors, setErrors] = useState({employee: "", cOffLeaves: "", reason: ""});
  const [reasonFocused, setReasonFocused] = useState(false);
  const [empSelectFocused, setEmpSelectFocused] = useState(false);
  const [cOffSelectFocused, setCOffSelectFocused] = useState(false);

  useEffect(() => {
    if (toast.open) {
      const t = setTimeout(() => setToast(p => ({...p, open: false})), 3000);
      return () => clearTimeout(t);
    }
  }, [toast.open]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await GET("admin/employeeDetails");
        if (response.status === 200 && Array.isArray(response.data.data)) {
          setEmployeeList(response.data.data);
        } else {
          setEmployeeList([]);
        }
      } catch (error) {
        setEmployeeList([]);
        setToast({
          open: true,
          color: "error",
          message: error.response?.data?.message || "Error fetching employees"
        });
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const valid =
      empId && cOffLeaves !== "" && reason.trim() !== "" && dates.every(d => d && d.isValid());
    setIsFormValid(valid);
  }, [empId, cOffLeaves, reason, dates]);

  const handleEmployeeChange = e => {
    setEmpId(Number(e.target.value));
    setErrors(prev => ({...prev, employee: ""}));
  };

  const handleCOffLeavesChange = e => {
    const val = e.target.value;
    setCOffLeaves(val);
    setErrors(prev => ({...prev, cOffLeaves: ""}));
    setDates(Array.from({length: val}, () => dayjs()));
  };

  const handleDateChange = (index, newDate) => {
    const updated = [...dates];
    updated[index] = newDate;
    setDates(updated);
  };

  const handleReasonChange = e => {
    setReason(e.target.value);
    if (e.target.value.trim()) setErrors(prev => ({...prev, reason: ""}));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const newErrors = {};
    if (!empId) newErrors.employee = "Please select an employee";
    if (!cOffLeaves) newErrors.cOffLeaves = "Please select total C-OFF days";
    if (!reason.trim()) newErrors.reason = "Reason is required";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      setToast({open: true, color: "error", message: "Please fill in all required fields."});
      return;
    }

    const payload = {
      emp_id: empId,
      total_coff: Number(cOffLeaves),
      reason,
      coffdates: dates.map(d => d.format("YYYY-MM-DD")),
      leaveAction: isAutoApproved ? "Approved" : "Raise Request"
    };

    try {
      const response = await POST("employee/applyCoff", payload);
      if (response.status === 200) {
        setToast({
          open: true,
          color: "success",
          message: response.data.message || "C-OFF request submitted successfully."
        });
        setCOffLeaves("");
        setDates([dayjs()]);
        setReason("");
        setEmpId("");
        setIsAutoApproved(true);
        setErrors({employee: "", cOffLeaves: "", reason: ""});
      }
    } catch (error) {
      const msg =
        error.response?.status === 400 ||
        error.response?.status === 401 ||
        error.response?.status === 403
          ? error.response.data?.message || error.response.data
          : "An error occurred while submitting the request.";
      setToast({open: true, color: "error", message: msg});
    }
  };

  return (
    <DashboardLayout>
      <style>{globalCSS}</style>
      <DashboardNavbar />

      <div className="aco-page aco-page-wrap" style={s.page}>
        <div className="aco-card">
          <div className="aco-header-inner" style={s.header}>
            <div style={s.hCircle1} />
            <div style={s.hCircle2} />
            <div style={s.headerIcon}>
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="#fff"
                strokeWidth="2.2"
                viewBox="0 0 24 24"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <p style={s.headerTitle}>Assign Compensatory Off</p>
            <p style={s.headerSub}>Assign C-OFF leaves to an employee for extra days worked</p>
          </div>

          <div className="aco-body-inner">
            <div className="aco-emp-row">
              <div className="aco-emp-select-wrap">
                <div style={s.fieldGroup}>
                  <label style={s.label}>
                    Employee<span style={s.required}>*</span>
                  </label>
                  <div style={s.selectWrap}>
                    <select
                      value={empId}
                      onChange={handleEmployeeChange}
                      onFocus={() => setEmpSelectFocused(true)}
                      onBlur={() => setEmpSelectFocused(false)}
                      style={{
                        ...s.select,
                        ...(errors.employee ? s.selectError : {}),
                        ...(empSelectFocused ? s.selectFocus : {})
                      }}
                    >
                      <option value="">Select employee</option>
                      {employeeList.length > 0 ? (
                        employeeList.map(emp => (
                          <option key={emp.empId} value={emp.empId}>
                            {emp.firstName} {emp.lastName} [{emp.empId}]
                          </option>
                        ))
                      ) : (
                        <option disabled>No employees available</option>
                      )}
                    </select>
                    <span style={s.selectArrow}>
                      <svg
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </div>
                  <FieldError msg={errors.employee} />
                </div>
              </div>

              <div className="aco-checkbox-wrap" style={{paddingTop: 22}}>
                <label style={s.autoApproveBadge(isAutoApproved)}>
                  <input
                    type="checkbox"
                    checked={isAutoApproved}
                    onChange={e => setIsAutoApproved(e.target.checked)}
                    style={{width: 15, height: 15, accentColor: "#3b55e6", cursor: "pointer"}}
                  />
                  {isAutoApproved ? (
                    <svg
                      width="13"
                      height="13"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  ) : (
                    <svg
                      width="13"
                      height="13"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                  )}
                  Auto Approve
                </label>
              </div>
            </div>

            <div style={s.fieldGroup}>
              <label style={s.label}>
                C-OFF Days<span style={s.required}>*</span>
              </label>
              <div style={s.selectWrap}>
                <select
                  value={cOffLeaves}
                  onChange={handleCOffLeavesChange}
                  onFocus={() => setCOffSelectFocused(true)}
                  onBlur={() => setCOffSelectFocused(false)}
                  style={{
                    ...s.select,
                    ...(errors.cOffLeaves ? s.selectError : {}),
                    ...(cOffSelectFocused ? s.selectFocus : {})
                  }}
                >
                  <option value="" disabled>
                    Select total days
                  </option>
                  {[1, 2, 3, 4, 5].map(num => (
                    <option key={num} value={num}>
                      {num} {num === 1 ? "day" : "days"}
                    </option>
                  ))}
                </select>
                <span style={s.selectArrow}>
                  <svg
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>
              <FieldError msg={errors.cOffLeaves} />
            </div>

            <div style={s.fieldGroup}>
              <label style={s.label}>
                Reason<span style={s.required}>*</span>
              </label>
              <textarea
                value={reason}
                onChange={handleReasonChange}
                onFocus={() => setReasonFocused(true)}
                onBlur={() => setReasonFocused(false)}
                placeholder="Describe why C-OFF is being assigned..."
                style={{
                  ...s.textarea,
                  ...(errors.reason ? s.textareaError : {}),
                  ...(reasonFocused ? s.textareaFocus : {})
                }}
              />
              <FieldError msg={errors.reason} />
            </div>

            {cOffLeaves !== "" && (
              <>
                <div style={s.divider} />
                <p style={s.sectionLabel}>C-OFF Dates</p>
                <table style={s.datesTable}>
                  <thead>
                    <tr>
                      <th className="aco-dates-th" style={{...s.datesTh, width: 40}}>
                        #
                      </th>
                      <th className="aco-dates-th" style={s.datesTh}>
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dates.map((date, index) => (
                      <tr key={index} className="aco-date-row">
                        <td className="aco-dates-td" style={s.datesTd}>
                          <span style={s.dayBadge}>{index + 1}</span>
                        </td>
                        <td className="aco-dates-td" style={s.datesTd}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <div className="aco-datepicker">
                              <DatePicker
                                format="DD/MM/YYYY"
                                value={date}
                                onChange={newDate => handleDateChange(index, newDate)}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    fullWidth: true,
                                    sx: {
                                      "& .MuiInputBase-root": {
                                        borderRadius: "10px",
                                        fontSize: "0.88rem",
                                        fontFamily: "'DM Sans', sans-serif"
                                      }
                                    }
                                  }
                                }}
                              />
                            </div>
                          </LocalizationProvider>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <button
              className="aco-btn-primary"
              style={s.btnPrimary}
              onClick={handleSubmit}
              disabled={!isFormValid}
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path d="M22 2L11 13" />
                <path d="M22 2L15 22 11 13 2 9l20-7z" />
              </svg>
              {isAutoApproved ? "Assign & Approve C-OFF" : "Submit C-OFF Request"}
            </button>
          </div>
        </div>
      </div>

      {toast.open && (
        <div
          className="aco-toast"
          style={toast.color === "success" ? s.toastSuccess : s.toastError}
        >
          {toast.color === "success" ? (
            <svg
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          ) : (
            <svg
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          )}
          {toast.message}
        </div>
      )}
    </DashboardLayout>
  );
}
